<template>
  <section class="mt-5 mb-5">
    <div class="container"> 
      <div class="row justify-content-center">
        <div class="col-md-8">
          <LocationPicker @location-selected="handleLocationSelected" />
        </div>
      </div>

      <!-- Loading Skeleton -->
      <div v-if="loading" class="row mt-4">
        <!-- Categories Skeleton -->
        <div class="col-12 mb-3">
          <div class="d-flex flex-wrap gap-2">
            <div v-for="i in 8" :key="`cat-skel-${i}`" class="skeleton-chip"></div>
          </div>
        </div>
        
        <!-- Vendor Cards Skeleton -->
        <div
          v-for="i in 6"
          :key="`vendor-skel-${i}`"
          class="col-12 col-md-6 mb-3"
        >
          <div class="card shadow-sm p-3 h-100 skeleton-card">
            <div class="card-body d-flex align-items-start">
              <div class="skeleton-logo rounded me-3"></div>
              <div class="flex-grow-1 min-w-0">
                <div class="skeleton-title mb-2"></div>
                <div class="skeleton-text mb-2"></div>
                <div class="skeleton-text short mb-2"></div>
                <div class="d-flex flex-wrap gap-1">
                  <div class="skeleton-badge"></div>
                  <div class="skeleton-badge"></div>
                </div>
              </div>
            </div>
            <div class="card-footer skeleton-footer">
              <div class="d-flex gap-2">
                <div class="skeleton-button"></div>
                <div class="skeleton-button"></div>
                <div class="skeleton-button"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error -->
      <div v-if="error && !loading" class="alert alert-danger mt-4">
        {{ error }}
        <button class="btn btn-sm btn-outline-danger py-2 px-3 ms-2" @click="retryLoading">Retry</button>
      </div>

      <!-- Categories (single line + expand) -->
      <div v-if="categories.length && !error && !loading" class="row mt-3">
        <div class="col-12">
          <div class="cats-wrap" :class="{ expanded: catsExpanded }">
            <button
              v-for="category in categories"
              :key="category.id"
              class="chip"
              :class="{ active: selectedCategory === category.id }"
              @click="selectCategory(category.id)"
              :title="`${category.name} (${category.count})`"
            >
              <span class="chip-label">{{ category.name }}</span>
              <span class="chip-count">{{ category.count }}</span>
            </button>

            <!-- Expand/Collapse -->
            <button
              v-if="showCatsToggle"
              class="chip chip-toggle"
              :aria-expanded="catsExpanded ? 'true' : 'false'"
              @click="catsExpanded = !catsExpanded"
              :title="catsExpanded ? 'Collapse' : 'Show more'"
            >
              <i :class="catsExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Vendors -->
      <div v-if="vendors.length && !error && !loading" class="row mt-3">
        <div
          v-for="vendor in vendors"
          :key="getVendorId(vendor)"
          class="col-12 col-md-6 mb-3"
        >
          <div class="card shadow-sm p-3 h-100">

            <!-- Card Body Clickable -->
            <router-link
              :to="`/${vendor.s_username}`"
              class="card-body d-flex align-items-start text-decoration-none text-dark"
            >
              <img
                :src="vendor.s_user_img || 'https://dummyimage.com/60x60/222/222.png'"
                alt="Vendor Logo"
                class="rounded me-3 vendor-logo"
                @error="handleImageError"
              />
              <div class="flex-grow-1 pl-2 min-w-0">
                <h5 class="mb-0 text-truncate h6">
                  {{ (vendor.s_business || vendor.s_name || '-') }}
                </h5>
                <p class="text-muted mb-1 addr" v-if="vendor.s_address">
                  <i class="fas fa-map-marker-alt mr-1"></i>
                  {{ vendor.s_address }} • {{ vendor.distance }}
                </p>

                <!-- Vendor categories -->
                <div class="vcat-wrap" :class="{ expanded: isVendorCatsExpandedByVendor(vendor) }">
                  <template
                    v-for="(vcat, idx) in visibleVendorCatsByVendor(vendor)"
                    :key="`${getVendorId(vendor)}-${vcat?.id ?? idx}`"
                  >
                    <span class="vcat-badge">
                      <i class="fas fa-check-circle mr-1"></i>{{ vcat?.name || '-' }}
                    </span>
                  </template>

                  <button
                    v-if="hiddenVendorCatsCountByVendor(vendor) > 0 && !isVendorCatsExpandedByVendor(vendor)"
                    class="vcat-more btn btn-link px-1"
                    @click.stop.prevent="toggleVendorCatsByVendor(vendor)"
                  >
                    +{{ hiddenVendorCatsCountByVendor(vendor) }} more
                  </button>

                  <button
                    v-if="isVendorCatsExpandedByVendor(vendor)"
                    class="vcat-more btn btn-link px-1"
                    @click.stop.prevent="toggleVendorCatsByVendor(vendor)"
                  >
                    Show less
                  </button>
                </div>
              </div>
            </router-link>

            <!-- Footer (Interactive Buttons) -->
            <div class="card-footer">
              <div class="quick-links d-flex overflow-auto">
                <button
                  v-for="(link, index) in vendor.newlinks || []"
                  :key="link.id || index"
                  class="ql-btn btn btn-sm me-2 d-flex align-items-center"
                  @click.stop="handleQuickLink(link, vendor)"
                  type="button"
                >
                  <img v-no-right-click 
                    :src="link.url" 
                    alt="" 
                    class="ql-icon me-1" 
                    @error="handleIconError"
                  />
                  <span class="ql-label">{{ link.title }}</span>
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-if="!loading && !vendors.length && selectedLocation && !error" class="row mt-4">
        <div class="col-12 text-center py-5">
          <img v-no-right-click 
            src="/assets/placeholders/no-vendors.png" 
            alt="No vendors" 
            style="max-width: 240px;" 
            class="mb-3"
            @error="handlePlaceholderError"
          > 
          <p class="text-muted">Try adjusting your location or search radius</p>
          <button class="btn btn-outline-primary mt-2" @click="resetLocation">
            Change Location
          </button>
        </div>
      </div>

      <!-- Initial State -->
      <div v-if="!selectedLocation && !loading" class="row mt-4">
        <div class="col-12 text-center py-5">
          <div class="text-muted">
            <i class="fas fa-map-marker-alt fa-3x mb-3 text-light"></i>
            <p>Select a location to find vendors near you</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import LocationPicker from '@/components/common/LocationPicker.vue'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'

const vendors = ref([])
const categories = ref([])
const loading = ref(false)
const selectedLocation = ref(null)
const selectedCategory = ref(0) // default "All"
const error = ref(null)
const catsExpanded = ref(false)

// Per-card expand state: { [vendorId]: true|false }
const expandedVendorState = ref({})

const session = useSessionStore()
const masterVendors = ref([])

// Add timeout for loading state
let loadingTimeout = null

/* ---------- Image Error Handling ---------- */
const handleImageError = (event) => {
  event.target.src = 'https://dummyimage.com/60x60/222/222.png'
}

const handleIconError = (event) => {
  event.target.style.display = 'none'
}

const handlePlaceholderError = (event) => {
  event.target.style.display = 'none'
  // Show icon instead
  const parent = event.target.parentElement
  if (parent) {
    const icon = document.createElement('i')
    icon.className = 'fas fa-store fa-3x mb-3 text-light'
    parent.insertBefore(icon, event.target.nextSibling)
  }
}

/* ---------- Categories helpers ---------- */
const showCatsToggle = computed(() => categories.value.length > 8)

function buildAllCategory(vendorList) {
  return { id: 0, name: 'All', count: vendorList.length }
}

function normalizeAndInjectAll(rawCats, vendorList) {
  const countsById = vendorList.reduce((acc, v) => {
    const arr = Array.isArray(v.categories) ? v.categories : []
    const ids = new Set(arr.map(c => Number(c?.id)))
    ids.forEach(id => {
      if (!Number.isNaN(id)) acc[id] = (acc[id] || 0) + 1
    })
    return acc
  }, {})

  let base = (rawCats || [])
    .filter(c => !(c.id === 0 || /^(all)$/i.test(String(c.name || '').trim())))
    .map(c => ({ ...c, count: countsById[c.id] || 0 }))

  return [buildAllCategory(vendorList), ...base]
}

/* ---------- Vendor categories (per card) ---------- */
const COLLAPSED_CAT_COUNT = 2

function getVendorId(v) {
  return v?.id ?? v?.s_id ?? v?.vendor_id ?? v?.s_vendor_id ?? `v-${Math.random()}`
}

function isVendorCatsExpandedByVendor(vendor) {
  const id = getVendorId(vendor)
  return !!expandedVendorState.value[id]
}

function toggleVendorCatsByVendor(vendor) {
  const id = getVendorId(vendor)
  expandedVendorState.value = {
    ...expandedVendorState.value,
    [id]: !expandedVendorState.value[id]
  }
}

function visibleVendorCatsByVendor(vendor) {
  const list = Array.isArray(vendor.categories) ? vendor.categories : []
  if (isVendorCatsExpandedByVendor(vendor)) return list
  return list.slice(0, COLLAPSED_CAT_COUNT)
}

function hiddenVendorCatsCountByVendor(vendor) {
  const list = Array.isArray(vendor.categories) ? vendor.categories : []
  return Math.max(0, list.length - COLLAPSED_CAT_COUNT)
}

/* ---------- API flows ---------- */
const handleLocationSelected = async (location) => {
  try {
    loading.value = true
    error.value = null
    catsExpanded.value = false
    selectedLocation.value = location
    selectedCategory.value = 0
    expandedVendorState.value = {}

    // Set a timeout to show skeleton even if API is slow
    loadingTimeout = setTimeout(() => {
      if (loading.value) {
        // Still loading after 1 second, skeleton will remain visible
        console.log('API is taking longer than expected...')
      }
    }, 1000)

    const vendorsFormData = new FormData()
    vendorsFormData.append('lat', location.lat)
    vendorsFormData.append('long', location.lng)
    vendorsFormData.append('user_id', session.userId)
    vendorsFormData.append('category_id', 0)

    const categoriesFormData = new FormData()
    categoriesFormData.append('lat', location.lat)
    categoriesFormData.append('long', location.lng)

    const [vendorsResponse, categoriesResponse] = await Promise.all([
      api.getVendors(vendorsFormData),
      api.getCategoriesList(categoriesFormData)
    ])

    const vList = vendorsResponse?.data?.list || []
    vendors.value = vList
    masterVendors.value = vList

    const rawCats = categoriesResponse?.data?.list || []
    categories.value = normalizeAndInjectAll(rawCats, masterVendors.value)
  } catch (err) {
    console.error('Error fetching vendors:', err)
    error.value = 'Failed to load vendors. Please try again.'
    vendors.value = []
    categories.value = []
  } finally {
    loading.value = false
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      loadingTimeout = null
    }
  }
}

function norm(str = "") {
  return String(str).trim();
}

function toTelHref(raw) {
  const digits = norm(raw).replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : "";
}

function toMailHref(raw) {
  const email = norm(raw);
  return email ? `mailto:${email}` : "";
}

function toMapsHref(raw) {
  const s = norm(raw);
  if (/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(s)) {
    return `https://www.google.com/maps?q=${encodeURIComponent(s)}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s)}`;
}

function openNew(url) {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function handleQuickLink(link, vendor) {
  const title = norm(link?.title).toLowerCase();
  const value = norm(link?.value);

  if (title.includes("call")) {
    const href = toTelHref(value);
    if (href) {
      window.location.href = href;
      return;
    }
  }

  if (title.includes("email")) {
    const href = toMailHref(value);
    if (href) {
      window.location.href = href;
      return;
    }
  }

  if (title.includes("direction")) {
    openNew(toMapsHref(value));
    return;
  }

  if (title.includes("enquiry") || title.includes("inquiry")) {
    if (vendor?.s_email) {
      window.location.href = toMailHref(vendor.s_email);
      return;
    }
  }

  if (/^https?:\/\//i.test(value)) {
    openNew(value);
  }
}

const selectCategory = async (categoryId) => {
  if (!selectedLocation.value || selectedCategory.value === categoryId) return
  try {
    loading.value = true
    error.value = null
    selectedCategory.value = categoryId
    expandedVendorState.value = {}

    const formData = new FormData()
    formData.append('lat', selectedLocation.value.lat)
    formData.append('long', selectedLocation.value.lng)
    formData.append('user_id', session.userId)
    formData.append('category_id', categoryId)

    const response = await api.getVendors(formData)
    vendors.value = response?.data?.list || []
  } catch (err) {
    console.error('Error fetching vendors by category:', err)
    error.value = 'Failed to filter vendors. Please try again.'
  } finally {
    loading.value = false
  }
}

const resetLocation = () => {
  selectedLocation.value = null
  vendors.value = []
  categories.value = []
  selectedCategory.value = 0
  catsExpanded.value = false
  expandedVendorState.value = {}
}

const retryLoading = () => {
  if (selectedLocation.value) handleLocationSelected(selectedLocation.value)
}

// Cleanup on unmount
onMounted(() => {
  return () => {
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
    }
  }
})
</script>

<style scoped>
/* ---------- Skeleton Loader Styles ---------- */
.skeleton-card {
  border: 1px solid #f0f0f0;
}

.skeleton-logo {
  width: 60px;
  height: 60px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

.skeleton-title {
  height: 16px;
  width: 70%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-text {
  height: 12px;
  width: 90%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-text.short {
  width: 60%;
}

.skeleton-badge {
  height: 20px;
  width: 60px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 10px;
  margin-right: 6px;
}

.skeleton-chip {
  height: 32px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 16px;
  margin: 4px;
}

.skeleton-footer {
  background: #f8f9fa;
  border-top: 1px solid #f0f0f0;
}

.skeleton-button {
  height: 30px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 15px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ---------- Cards ---------- */
.card {
  transition: transform 0.2s, box-shadow 0.2s;
  border-radius: 12px;
  overflow: hidden;
}
.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 20px rgba(0,0,0,.08);
}
.vendor-logo { width: 60px; height: 60px; object-fit: cover; }
.addr { font-size: 13px; max-width: 100%; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.min-w-0 { min-width: 0; }

/* ---------- Categories: single line + expand ---------- */
.cats-wrap {
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  overflow: hidden;
  gap: .4rem;
  padding-right: 40px;
  margin: 0 -.25rem;
}
.cats-wrap::after {
  content: "";
  position: absolute;
  right: 0; top: 0; bottom: 0;
  width: 160px;
  z-index:10;
  pointer-events: none;
  background: linear-gradient(90deg, rgba(255,255,255,0), #ffffff 60%);
}
.cats-wrap.expanded {
  flex-wrap: wrap;
  overflow: visible;
  padding-right: 0;
}
.cats-wrap.expanded::after { display: none; }

.chip {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  border: 1px solid #FB721030;
  background: #f7fbff;
  color: #FB7210;
  font-weight: 600;
  font-size: 12px;
  padding: .35rem .55rem;
  border-radius: 999px;
  cursor: pointer;
  transition: background .15s ease, color .15s ease, border-color .15s ease;
  margin: .25rem;
}
.chip:hover { background: #FB721020; }
.chip.active { background: #FB7210; color: #fff; border-color: #FB7210; }
.chip-label { white-space: nowrap; }
.chip-count {
  background: #FB721030;
  color: #FB7210;
  border-radius: 999px;
  padding: .05rem .35rem;
  font-weight: 700;
  font-size: 11px;
}
.chip.active .chip-count { background: rgba(0,0,0,.15); color: #fff; }

.chip-toggle {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 50%;
  z-index: 12;
  justify-content: center;
}
.cats-wrap.expanded .chip-toggle {
  position: static; 
  width: 32px!important; height: 32px!important;
  transform: translateY(15%);
  padding: .35rem .55rem;
  background: lightgrey;
  color: #222;
}

/* ---------- Vendor categories (per-card expand) ---------- */
.vcat-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem .35rem;
  align-items: center;
}
.vcat-badge {
  background: #f5f7fb;
  border: 1px solid #e1e8f5;
  color: #22355e;
  border-radius: 8px;
  padding: .2rem .45rem;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.vcat-badge i { color: #FB7210; }
.vcat-more {
  border: 1px dashed #bcd0f7;
  background: #fff;
  color: #FB7210;
  border-radius: 8px;
  padding: .2rem .5rem;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

/* ---------- Quick links (horizontal scroll) ---------- */
.card-footer { background: #FB721010; border-top: 1px solid #f0f3fa; }
.quick-links {
  display: flex;
  gap: .5rem;
  overflow-x: auto;
  padding: .25rem 0;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
}
.quick-links::-webkit-scrollbar { height: 8px; }
.quick-links::-webkit-scrollbar-thumb { background: #d7e2f3; border-radius: 4px; }
.ql-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: .4rem;
  border: 1px solid #FB721020;
  color: #FB7210;
  background: #fff;
  padding: .35rem .6rem;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
  scroll-snap-align: start;
  transition: background .15s ease, border-color .15s ease;
}
.ql-btn:hover { background: #f3f8ff; border-color: #a9c5ff; }
.ql-icon { width: 18px; height: 18px; object-fit: contain; }
.ql-label { line-height: 1; }

@media (max-width: 575.98px) {
  .vendor-logo { width: 48px; height: 48px; }
  .skeleton-logo { width: 48px; height: 48px; }
}
</style>